import { api, apiUrl } from './api';

export interface AssetSummary {
  id: string;
  kind: 'image' | 'audio';
  filename: string;
  description: string;
  caption?: string;
  width?: number;
  height?: number;
  aspectRatio?: number;
  /**
   * The source carried more than one frame.
   *
   * Decided at upload and sent by the server, because nothing on this side can tell: a `.webp`
   * may or may not move and the mime type does not say. Everything that treats an animation
   * differently — the play control, the motion description, the reduced-motion swap — keys on
   * this rather than sniffing a URL.
   */
  animated?: boolean;
  frameCount?: number;
  /**
   * Flashes per second at its worst, measured once when the file was uploaded.
   *
   * A MEASUREMENT, not a verdict. WCAG 2.3.1's threshold is defined over a proportion of the
   * viewer's visual field, which depends on how large the picture is drawn on a screen we know
   * nothing about — so the library says "this flashes N times a second" and never "this fails".
   * Absent on an asset uploaded before the check existed, which is why nothing may be shown rather
   * than a reassuring zero.
   */
  flashesPerSecond?: number;
  /** One pass through the frames. Absent when the source declared no frame delays. */
  durationMs?: number;
  durationSeconds?: number;
  // Small (~40-value) peak-amplitude summary of the final processed audio, for a static
  // thumbnail waveform in library grids — audio assets only, absent for images.
  waveformPeaks?: number[];
  /** `poster` is the still first frame, present only on an animated image — it is what the play
   *  control pauses to and what `prefers-reduced-motion` shows, since an `<img>` cannot be paused. */
  urls: { display?: string; thumb?: string; original: string; audio?: string; poster?: string };
  /**
   * The record is here but its bytes are not, so every URL above will 404.
   *
   * Sent by the server, which is the only side that can tell. Without it the library showed a
   * blank tile and there was no way to tell a broken asset from a slow one — seventeen of
   * twenty-seven rows in one real library were in that state, silently.
   */
  broken?: boolean;
  createdAt: string;
}

interface RawAssetSummary extends Omit<AssetSummary, 'urls'> {
  urls: { display?: string; thumb?: string; original: string; audio?: string; poster?: string };
}

function resolveAsset(raw: RawAssetSummary): AssetSummary {
  return {
    ...raw,
    urls: {
      display: raw.urls.display ? apiUrl(raw.urls.display) : undefined,
      thumb: raw.urls.thumb ? apiUrl(raw.urls.thumb) : undefined,
      original: apiUrl(raw.urls.original),
      audio: raw.urls.audio ? apiUrl(raw.urls.audio) : undefined,
      // Built field by field, so anything missing here is silently dropped however well the type
      // above describes it.
      poster: raw.urls.poster ? apiUrl(raw.urls.poster) : undefined,
    },
  };
}

export interface AssetUsageSummary {
  blockCount: number;
  experienceCount: number;
}

export interface AssetUsageDetail {
  experienceId: string;
  experienceTitle: string;
  taskId: string;
  taskTitle: string;
  blockId: string;
  /** Present only for a per-option usage (an MCQ/Ordering option's own media) — absent for a
   * block-level usage (Image/Sound blocks). */
  optionId?: string;
  altText?: string;
  caption?: string;
  transcript?: string;
  /** Only ever set on an ANIMATED image's placement; absent everywhere else. */
  motionDescription?: string;
}

export interface TrashedAsset extends AssetSummary {
  deletedAt: string;
  purgeAt: string;
}

interface RawTrashedAsset extends RawAssetSummary {
  deletedAt: string;
  purgeAt: string;
}

export interface AudioEditParams {
  trimStartSeconds?: number;
  trimEndSeconds?: number;
  normalize?: boolean;
  fadeInSeconds?: number;
  fadeOutSeconds?: number;
}

function buildUploadForm(
  file: File,
  kind: 'image' | 'audio',
  meta?: { description?: string; caption?: string } & AudioEditParams,
): FormData {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('kind', kind);
  if (meta?.description) fd.append('description', meta.description);
  if (meta?.caption) fd.append('caption', meta.caption);
  if (meta?.trimStartSeconds != null) fd.append('trimStartSeconds', String(meta.trimStartSeconds));
  if (meta?.trimEndSeconds != null) fd.append('trimEndSeconds', String(meta.trimEndSeconds));
  if (meta?.normalize != null) fd.append('normalize', String(meta.normalize));
  if (meta?.fadeInSeconds != null) fd.append('fadeInSeconds', String(meta.fadeInSeconds));
  if (meta?.fadeOutSeconds != null) fd.append('fadeOutSeconds', String(meta.fadeOutSeconds));
  return fd;
}


/**
 * Resolved assets, kept for the life of the tab.
 *
 * ## Why this exists
 *
 * A block that holds an `assetId` can render NOTHING until `/assets/:id` comes back — the id is
 * all that is persisted, so the `<img>` has no src until then. Six call sites across four block
 * types (image, mcq options, ordering items, the library) each fetched on every mount, with no
 * memory between them. Walking quickly between tasks therefore meant most images never appeared:
 * not because a request failed, but because it had not landed before the teacher moved on, and
 * the next visit started again from nothing.
 *
 * ## Why it is safe to keep
 *
 * `resolveAsset` turns the payload into plain API paths (`/api/assets/:id/display`) — these are
 * NOT signed, time-limited URLs, so a cached entry cannot rot into a broken image. What can go
 * stale is the metadata beside them (filename, description, caption), and the only things that
 * change those are in this same file: `update` and `upload` write the fresh copy back, `remove`
 * and `purgeForever` evict. A rename made in another tab is the one case this will not see, and
 * it costs a stale caption rather than a missing picture.
 *
 * `inFlight` de-duplicates concurrent requests for the same id, which is the other half of the
 * problem: several blocks sharing one asset, or a fast there-and-back, used to issue a request
 * each.
 */
const assetCache = new Map<string, AssetSummary>();
const inFlight = new Map<string, Promise<AssetSummary>>();

/** Synchronous peek, for a caller that can seed its state and skip the empty first render. */
export function peekAsset(assetId: string): AssetSummary | undefined {
  return assetCache.get(assetId);
}

/** Drop an entry — used where this file mutates or removes an asset. */
function forgetAsset(assetId: string): void {
  assetCache.delete(assetId);
  inFlight.delete(assetId);
}

export const assetsApi = {
  list: async (
    kind: 'image' | 'audio' | 'any',
    opts?: { search?: string; experienceId?: string; limit?: number; skip?: number },
  ): Promise<{ items: AssetSummary[]; hasMore: boolean }> => {
    const params = new URLSearchParams();
    params.set('kind', kind);
    if (opts?.search) params.set('search', opts.search);
    if (opts?.experienceId) params.set('experienceId', opts.experienceId);
    if (opts?.limit) params.set('limit', String(opts.limit));
    if (opts?.skip) params.set('skip', String(opts.skip));
    const raw = await api.get<{ items: RawAssetSummary[]; hasMore: boolean }>(`/assets?${params.toString()}`);
    return { items: raw.items.map(resolveAsset), hasMore: raw.hasMore };
  },

  trash: async (kind: 'image' | 'audio' | 'any'): Promise<TrashedAsset[]> => {
    const raw = await api.get<RawTrashedAsset[]>(`/assets/trash?kind=${kind}`);
    return raw.map((a) => ({ ...resolveAsset(a), deletedAt: a.deletedAt, purgeAt: a.purgeAt }));
  },

  restore: async (assetId: string): Promise<AssetSummary> => {
    const restored = resolveAsset(await api.post<RawAssetSummary>(`/assets/${assetId}/restore`));
    assetCache.set(assetId, restored);
    return restored;
  },

  purgeForever: (assetId: string) => { forgetAsset(assetId); return api.delete(`/assets/${assetId}/permanent`); },

  get: async (assetId: string): Promise<AssetSummary> => {
    const cached = assetCache.get(assetId);
    if (cached) return cached;
    const pending = inFlight.get(assetId);
    if (pending) return pending;
    const request = api.get<RawAssetSummary>(`/assets/${assetId}`)
      .then((raw) => { const a = resolveAsset(raw); assetCache.set(assetId, a); return a; })
      .finally(() => { inFlight.delete(assetId); });
    inFlight.set(assetId, request);
    return request;
  },

  upload: async (file: File, meta?: { description?: string; caption?: string }): Promise<AssetSummary & { deduped: boolean }> => {
    const raw = await api.postForm<RawAssetSummary & { deduped: boolean }>('/assets', buildUploadForm(file, 'image', meta));
    return { ...resolveAsset(raw), deduped: raw.deduped };
  },

  uploadAudio: async (
    file: File,
    meta?: { description?: string } & AudioEditParams,
  ): Promise<AssetSummary & { deduped: boolean }> => {
    const raw = await api.postForm<RawAssetSummary & { deduped: boolean }>('/assets', buildUploadForm(file, 'audio', meta));
    return { ...resolveAsset(raw), deduped: raw.deduped };
  },

  usage: (assetId: string): Promise<AssetUsageSummary> => api.get<AssetUsageSummary>(`/assets/${assetId}/usage`),

  usageDetails: (assetId: string): Promise<AssetUsageDetail[]> => api.get<AssetUsageDetail[]>(`/assets/${assetId}/usage-details`),

  swap: async (assetId: string, file: File, editParams?: AudioEditParams): Promise<{ asset: AssetSummary; usage: AssetUsageSummary }> => {
    // `kind` for the form is irrelevant here — the backend dispatches swap() on the asset's
    // own already-loaded kind, not on anything the client sends.
    const raw = await api.postForm<{ asset: RawAssetSummary; usage: AssetUsageSummary }>(
      `/assets/${assetId}/swap`,
      buildUploadForm(file, 'image', editParams),
    );
    return { asset: resolveAsset(raw.asset), usage: raw.usage };
  },

  update: async (assetId: string, patch: { filename?: string; description?: string; caption?: string }): Promise<AssetSummary> => {
    const updated = resolveAsset(await api.patch<RawAssetSummary>(`/assets/${assetId}`, patch));
    assetCache.set(assetId, updated);   // the fresh copy, not an eviction: it is in hand already
    return updated;
  },

  remove: (assetId: string) => { forgetAsset(assetId); return api.delete(`/assets/${assetId}`); },
};
